// Icon set: solid glyphs from Heroicons (https://heroicons.com) and stroke
// glyphs from Lucide (https://lucide.dev), both on a 24x24 grid.

export interface IconProps {
  /** Defaults to a 16px square that will not shrink in a flex row. */
  className?: string;
}

const DEFAULT_SIZE = "size-4 shrink-0";

const SOLID = {
  viewBox: "0 0 24 24",
  fill: "currentColor",
  "aria-hidden": true,
} as const;

const STROKE = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

/** heroicons: musical-note (solid) */
export function MusicIcon(props: IconProps) {
  return <svg {...SOLID} className={props.className ?? DEFAULT_SIZE}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19.952 1.651a.75.75 0 0 1 .298.599V16.303a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.403-4.909l2.311-.66a1.5 1.5 0 0 0 1.088-1.442V6.994l-9 2.572v9.737a3 3 0 0 1-2.176 2.884l-1.32.377a2.553 2.553 0 1 1-1.402-4.909l2.31-.66a1.5 1.5 0 0 0 1.088-1.442V5.25a.75.75 0 0 1 .544-.721l10.5-3a.75.75 0 0 1 .658.122Z" />
  </svg>
}

/** heroicons: question-mark-circle (solid) */
export function QuestionMarkCircleIcon(props: IconProps) {
  return <svg {...SOLID} className={props.className ?? DEFAULT_SIZE}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
  </svg>
}

/** lucide: list-music */
export function ListMusicIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <path d="M16 5H3" />
    <path d="M11 12H3" />
    <path d="M11 19H3" />
    <path d="M21 16V5" />
    <circle cx="18" cy="16" r="3" fill="currentColor" />
  </svg>
}

/** lucide: calendar */
export function CalendarIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M3 10h18" />
  </svg>
}

/** lucide: chevron-left */
export function ChevronBackIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <path d="m15 18-6-6 6-6" />
  </svg>
}

/** lucide: chevron-right */
export function ChevronForwardIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <path d="m9 18 6-6-6-6" />
  </svg>
}

/** lucide: share (SF Symbol: square.and.arrow.up) */
export function ShareIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <path d="M12 2v13" />
    <path d="m16 6-4-4-4 4" />
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
  </svg>
}

/** Solid rounded square with a play cutout (SF Symbol: play.rectangle.fill). */
export function PlayRectangleIcon(props: IconProps) {
  return <svg {...SOLID} className={props.className ?? DEFAULT_SIZE}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5Zm6.5 3.4v7.2a.6.6 0 0 0 .9.52l6-3.6a.6.6 0 0 0 0-1.04l-6-3.6a.6.6 0 0 0-.9.52Z" />
  </svg>
}

/** lucide: x */
export function XIcon(props: IconProps) {
  return <svg {...STROKE} className={props.className ?? DEFAULT_SIZE}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
}
